import { G } from '@mobily/ts-belt';
import { useCallback, useEffect } from 'react';
import { required, TextInput, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';

import { ShowSection } from '@sh4res/bo-common/components';
import { voluntaryPaymentTypeChoices } from '@sh4res/bo-common/components/B2bVoluntaryPayment/choices';
import { RadioButtonGroupInput } from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { B2bCompanySelectInput, B2bEmployeeSelectInput } from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { inputDisabledProps } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bVoluntaryPaymentCreateGeneralInfoStep';

export const AffiliateInformationSection = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const resourceBasePath = useResourceBasePath();

  const [companyId, employeeId]: (number | null)[] = useWatch({
    name: ['b2b_company_id', 'b2b_employee_id'],
    defaultValue: {
      b2b_company_id: null,
      b2b_employee_id: null,
    },
  });

  const clearEmployee = useCallback(() => {
    setValue('firstname', null);
    setValue('lastname', null);
    setValue('social_status', null);
    setValue('nir', null);
    setValue('ntt', null);
    setValue('user_id', null);
    setValue('contract_ids', null);
    setValue('sources', []);
    setValue('targets', []);
  }, [setValue]);

  useEffect(() => {
    if (G.isNullable(employeeId)) {
      clearEmployee();
    }
  }, [employeeId, clearEmployee]);

  const handleCompanyChange = () => {
    setValue('b2b_employee_id', null);
    clearEmployee();
  };

  const handleEmployeeChange = (id: number | '', record: GetB2bEmployeesOneResponse | '') => {
    if (G.isNullable(id) || id === '' || record === '') {
      clearEmployee();
    } else {
      setValue('firstname', record.firstname);
      setValue('lastname', record.lastname);
      setValue('social_status', record.social_status);
      setValue('nir', record.nir);
      setValue('ntt', record.ntt);
      setValue('user_id', record.user_id);
      setValue('contract_ids', record.contract_ids);
      setValue('sources', []);
      setValue('targets', []);
    }
  };

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.voluntary_payment_create.sections.affiliate_information"
      noCard={true}>
      <B2bCompanySelectInput
        source="b2b_company_id"
        validate={[required()]}
        label="resources.bo_common.b2b.voluntary_payment_create.fields.b2b_company_id"
        onChange={handleCompanyChange}
        testId={`${testId}_CompanySelectInput`}
      />
      <B2bEmployeeSelectInput
        testId={`${testId}_EmployeeSelectInput`}
        source="b2b_employee_id"
        reference={`${resourceBasePath}employees`}
        label="resources.bo_common.b2b.voluntary_payment_create.fields.b2b_employee_id_search"
        validate={[required()]}
        filter={{ company_id: !G.isNullable(companyId) ? companyId : 0 }}
        shouldRenderSuggestions={() => !G.isNullable(companyId)}
        noOptionsText={translate(
          'resources.bo_common.b2b.voluntary_payment_create.messages.no_employees_found',
        )}
        onChange={handleEmployeeChange}
        {...inputDisabledProps(G.isNullable(companyId))}
      />
      <TextInput
        data-testid={`${testId}_FirstnameInput`}
        source="firstname"
        label="resources.bo_common.b2b.employees.fields.firstname"
        {...inputDisabledProps(true)}
      />
      <TextInput
        data-testid={`${testId}_LastnameInput`}
        source="lastname"
        label="resources.bo_common.b2b.employees.fields.lastname"
        {...inputDisabledProps(true)}
      />
      <TextInput
        data-testid={`${testId}_NirInput`}
        source="nir"
        label="resources.bo_common.b2b.employees.fields.nir"
        {...inputDisabledProps(true)}
      />
      <TextInput
        data-testid={`${testId}_NttInput`}
        source="ntt"
        label="resources.bo_common.b2b.employees.fields.ntt"
        {...inputDisabledProps(true)}
      />
      <TextInput
        data-testid={`${testId}_SocialStatusInput`}
        source="social_status"
        label="resources.bo_common.b2b.employees.fields.social_status"
        {...inputDisabledProps(true)}
      />
    </ShowSection>
  );
};

export const GeneralInfoStep = () => (
  <>
    <AffiliateInformationSection />
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.voluntary_payment_create.sections.type"
      subtitle="resources.bo_common.b2b.voluntary_payment_create.messages.type_subtitle"
      noCard={true}>
      <RadioButtonGroupInput
        data-testid={`${testId}_TypeInput`}
        source="type"
        choices={voluntaryPaymentTypeChoices}
        required
      />
    </ShowSection>
  </>
);
